/**
 * PushEngage Custom Events Handler
 *
 * Manages custom PushEngage triggered campaigns based on customer metafields
 * and page-behaviour signals.
 *
 * Events:
 *  1. loosegemstonedropoff    - Loose gemstone collection visitors with >= 2 purchases
 *  2. Chairmanvaultdropoff    - Customers with $1000+ orders in last year
 *  3. 3rdpurchase             - total_order_count >= thirdPurchaseMinOrders
 *  4. 2ndpurchase             - total_order_count >= secondPurchaseMinOrders && < secondPurchaseMaxOrders
 *  5. 60daysinactive          - Customers inactive for 60+ days
 *  6. notpurchased            - Registered customers with 0 purchases (once per session)
 *  7. categorydropoff         - PLP visitors after configured minutes
 *  8. browseabandonment60218  - PDP visitors without add-to-cart after configured minutes
 *                               Start event_name: "browse"
 *                               Stop  event_name: "add-to-cart"
 *  9. webtvdropoff            - Live TV page visitors after configured minutes
 *                               Start event_name: "livetv_browse_event"
 *                               Stop  event_name: "livetv_activity_event"
 * 10. Notify Me (addAlert)    - PushEngage.addAlert() on click of [data-notifydata] button
 *                               (back-in-stock / inventory alert)
 * 11. Price Drop (addAlert)   - PushEngage.addAlert() with type "price_drop" on CartAddEvent
 *                               Registers alert so customer is notified when price drops
 * 12. cartabandonment60218    - PDP add-to-cart stop signal
 *                               event_name: "add-to-cart"
 *                               Fires on CartAddEvent (via PDP add-to-cart click) so
 *                               PushEngage can stop the cart-abandonment drip.
 */


(function () {
  'use strict';

  // @ts-ignore
  window.ShopLCPushEngage = window.ShopLCPushEngage || {};

  // ---------------------------------------------------------------------------
  // Configuration (populated by pushengage-data.liquid before this script runs)
  // ---------------------------------------------------------------------------

  // @ts-ignore
  const config = window.ShopLCPushEngage.config || {
    enabled: false,
    customerLoggedIn: false,
    customerId: null,
    customerEmail: null,
    customerMetafields: {},
    currentTemplate: '',
    pageHandle: '',
    collectionHandle: '',
    collectionName: '',
    collectionLink: '',
    categoryDropoffMinutes: 2,
    secondPurchaseMinOrders: 1,
    secondPurchaseMaxOrders: 2,
    thirdPurchaseMinOrders: 2,
    browseAbandonmentMinutes: 1,
    product: null,
    webTVPageHandles: ['live-tv'],
    webTVDropoffMinutes: 1,
    looseGemstoneCollections: ['loose-gemstones'],
    chairmanVaultCollections: [],
    chairmanVaultMinOrderValue: 1000,
    inactiveRegisterDeviceUrl: '/apps/common/v1/register-device',
    debug: false,
    events: {
      looseGemstoneEnabled: true,
      chairmanVaultEnabled: true,
      purchaseCampaignsEnabled: true,
      inactiveEnabled: true,
      notPurchasedEnabled: true,
      categoryDropoffEnabled: true,
      browseAbandonmentEnabled: true,
      webTVDropoffEnabled: true,
      notifyMeEnabled: true,
      priceDropEnabled: true,
      cartAbandonmentEnabled: true,
    },
  };

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  const SESSION_KEYS = {
    NOT_PURCHASED_FIRED:    'pe_notpurchased_fired',
    CATEGORY_DROPOFF_FIRED: 'pe_categorydropoff_fired',
    PLP_ENTRY_TIME:         'pe_plp_entry_time',
  };

  // ---------------------------------------------------------------------------
  // Logger (no-op unless config.debug is true)
  // ---------------------------------------------------------------------------

  const logger = {
    // @ts-ignore
    log:   function (...a) { if (config.debug) console.log('[PushEngage]', ...a); },
    // @ts-ignore
    warn:  function (...a) { if (config.debug) console.warn('[PushEngage]', ...a); },
    // @ts-ignore
    error: function (...a) { console.error('[PushEngage]', ...a); },
  };

  // ---------------------------------------------------------------------------
  // Core: fire a PushEngage trigger event
  // ---------------------------------------------------------------------------

  /**
   * Send a trigger event via the PushEngage SDK.
   *
   * @param {{ campaign_name: string, event_name: string, profile_id?: string, data?: Record<string, string> }} trigger
   *
   * Uses window.PushEngage.push() so events are queued if the SDK has not yet
   * loaded; the SDK processes the queue once it initialises.
   *
   * IMPORTANT: Never cache window.PushEngage before calling push() — the SDK
   * replaces the pre-load array with the real object when it loads, so we must
   * read window.PushEngage fresh inside the push() callback.
   */
  function fireEvent(trigger) {
    // @ts-ignore
    window.PushEngage = window.PushEngage || [];

    if (config.customerEmail) {
      trigger.profile_id = config.customerEmail;
    }

    const eventName = trigger.event_name;
    logger.log('Firing:', eventName, trigger);

    try {
      // @ts-ignore
      window.PushEngage.push(function () {
        // @ts-ignore — window.PushEngage is now the real SDK object
        window.PushEngage.sendTriggerEvent(trigger)
          .then(function (/** @type {any} */ res) {
            logger.log('Sent:', eventName, res);
          })
          .catch(function (/** @type {{ message?: string, details?: any }} */ err) {
            logger.error('sendTriggerEvent failed:', eventName, err.message, err.details);
          });
      });
    } catch (err) {
      logger.error('Error firing event:', eventName, err);
    }
  }

  // ---------------------------------------------------------------------------
  // Metafield helpers
  // ---------------------------------------------------------------------------

  /**
   * @param {string} key
   * @returns {*}
   */
  function getMetafield(key) {
    return config.customerMetafields[key] ?? null;
  }

  /**
   * @param {string} key
   * @returns {number}
   */
  function getMetafieldNumber(key) {
    const val = getMetafield(key);
    if (val === null || val === undefined) return 0;
    const n = parseFloat(val);
    return isNaN(n) ? 0 : n;
  }

  // ---------------------------------------------------------------------------
  // Session helpers
  // ---------------------------------------------------------------------------

  /**
   * @param {string} key
   * @returns {boolean}
   */
  function sessionFired(key) {
    try { return sessionStorage.getItem(key) === 'true'; } catch (e) { return false; }
  }

  /** @param {string} key */
  function markSessionFired(key) {
    try { sessionStorage.setItem(key, 'true'); } catch (e) { logger.warn('sessionStorage unavailable:', e); }
  }

  // ---------------------------------------------------------------------------
  // Shared helper: match a handle against a configured list
  // (case-insensitive; checks exact match or substring)
  // ---------------------------------------------------------------------------

  /**
   * @param {string} handle
   * @param {string[]} list
   * @returns {boolean}
   */
  function matchesHandleList(handle, list) {
    const h = handle.toLowerCase();
    return list.some(function (item) {
      const t = item.trim().toLowerCase();
      return t !== '' && (h === t || h.includes(t));
    });
  }

  // ---------------------------------------------------------------------------
  // Shared: dwell-time tracker used by browseAbandonment and webTVDropoff
  //
  // After `thresholdMs` with no add-to-cart signal → calls onStart().
  // On any add-to-cart signal before the timer fires → calls onStop().
  // ---------------------------------------------------------------------------

  /**
   * @param {{ thresholdMs: number, onStart: () => void, onStop: () => void }} opts
   */
  function createDwellTracker(opts) {
    var handled = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    var timer = null;

    function fireStop() {
      if (handled) return;
      handled = true;
      if (timer !== null) { clearTimeout(timer); timer = null; }
      cleanup();
      opts.onStop();
    }

    /** @param {Event} e */
    function onCartUpdate(e) {
      const detail = /** @type {CustomEvent} */ (e).detail || {};
      // Ignore internal cart updates from our own components
      if (detail.source === 'cart-progress-bar' || detail.source === 'bv-cart-progress' || detail.source === 'secure-shield-cart') return;
      fireStop();
    }

    function cleanup() {
      document.removeEventListener('cart:update', onCartUpdate);
      document.removeEventListener('cart:updated', onCartUpdate);
    }

    // Detect ATC via product form submission
    const productForm = document.querySelector('form[action="/cart/add"]');
    if (productForm) {
      productForm.addEventListener('submit', fireStop, { once: true });
    }

    document.addEventListener('cart:update', onCartUpdate);
    document.addEventListener('cart:updated', onCartUpdate);

    window.addEventListener('beforeunload', function () {
      if (timer !== null) clearTimeout(timer);
      cleanup();
    }, { once: true });

    // Fire start event after dwell threshold (if ATC hasn't occurred first)
    timer = setTimeout(function () {
      timer = null;
      if (!handled) opts.onStart();
    }, opts.thresholdMs);
  }

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  function handleLooseGemstoneDropoff() {
    if (!config.events.looseGemstoneEnabled) return;
    if (config.currentTemplate !== 'collection') return;
    if (!matchesHandleList(config.collectionHandle, config.looseGemstoneCollections || ['loose-gemstones'])) return;

    const count = getMetafieldNumber('loose_gemstone_last6_mon_purchase');
    logger.log('Loose gemstone purchases (last 6mo):', count);

    if (count >= 2) {
      var trigger = {
        campaign_name: 'loosegemstonedropoff',
        event_name:    'category_browse_event',
        // profile_id: '<string>'  // optional — auto-set from config.customerEmail
      };
      fireEvent(trigger);
      logger.log('Fired loosegemstonedropoff');
    }
  }

  function handleChairmanVaultDropoff() {
    if (!config.events.chairmanVaultEnabled) return;

    // Optional: restrict to specific collection pages
    const restricted = (config.chairmanVaultCollections || []).filter(
      /** @param {string} h */ function (h) { return h.trim() !== ''; }
    );
    if (restricted.length > 0) {
      if (config.currentTemplate !== 'collection') return;
      if (!matchesHandleList(config.collectionHandle, restricted)) return;
    }

    const raw = getMetafield('last_1_year_1000_value_order');
    const threshold = config.chairmanVaultMinOrderValue || 1000;
    const isHighValue =
      (typeof raw === 'number' && raw >= threshold) ||
      (typeof raw === 'string' && parseFloat(raw) >= threshold);

    logger.log('Chairman vault metafield:', raw, '→ isHighValue:', isHighValue);

    if (isHighValue) {
      var trigger = {
        campaign_name: 'Chairmanvaultdropoff',
        event_name:    'category_browse_event',
        // profile_id: '<string>'  // optional — auto-set from config.customerEmail
      };
      fireEvent(trigger);
      logger.log('Fired Chairmanvaultdropoff');
    }
  }

  function handleThirdPurchase() {
    if (!config.events.purchaseCampaignsEnabled) return;

    const count = getMetafieldNumber('total_order_count');
    const min = config.thirdPurchaseMinOrders || 2;
    logger.log('3rd purchase — orderCount:', count, '| threshold >=', min);

    if (count >= min) {
      var trigger = {
        campaign_name: '3rdpurchase',
        event_name:    '3rdpurchase',
        // profile_id: '<string>'  // optional — auto-set from config.customerEmail
      };
      fireEvent(trigger);
      logger.log('Fired 3rdpurchase');
    }
  }

  function handleSecondPurchase() {
    if (!config.events.purchaseCampaignsEnabled) return;

    const count = getMetafieldNumber('total_order_count');
    const min = config.secondPurchaseMinOrders || 1;
    const max = config.secondPurchaseMaxOrders || 2;
    logger.log('2nd purchase — orderCount:', count, '| threshold >=', min, '&&<', max);

    if (count >= min && count < max) {
      var trigger = {
        campaign_name: '2ndpurchase',
        event_name:    '2ndpurchase',
        // profile_id: '<string>'  // optional — auto-set from config.customerEmail
      };
      fireEvent(trigger);
      logger.log('Fired 2ndpurchase');
    }
  }

  /**
   * Register Device — POST the PushEngage subscriber ID to the backend once per day.
   *
   * Endpoint: POST (configurable via theme — inactiveRegisterDeviceUrl, default /apps/common/v1/register-device)
   * Payload:  { deviceToken: <subscriberId> }
   *
   * - Runs only for logged-in customers.
   * - Uses localStorage to ensure the call is made at most once per calendar day.
   *   The date is only recorded after a successful API response, so a failed
   *   request will be retried on the next page load.
   */
  function handleSixtyDaysInactive() {
    logger.log('handleSixtyDaysInactive: start');
    if (!config.events.inactiveEnabled) return;

    if (!config.customerLoggedIn) return;
    logger.log('handleSixtyDaysInactive: Customer logged in');
    // call custom app team's api and custom app team handle this event through cron job
    // this will call once in a day
    var LS_KEY = 'pe_register_device_date';
    var today  = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'

    try {
      if (localStorage.getItem(LS_KEY) === today) {
        logger.log('Register device already called today, skipping');
        return;
      }
    } catch (e) {
      // localStorage unavailable — proceed anyway
      logger.log('handleSixtyDaysInactive: localStorage unavailable', e);
    }

    logger.log('handleSixtyDaysInactive: PushEngage initialized');
    // @ts-ignore
    window.PushEngage = window.PushEngage || [];

    // push() callback is async so we can await getSubscriberId() and fetch()
    // @ts-ignore
    window.PushEngage.push(async function () {
      logger.log('handleSixtyDaysInactive: PushEngage push callback');
      try {
        // @ts-ignore
        var deviceToken = await window.PushEngage.getSubscriberId();
  
        logger.log('handleSixtyDaysInactive: deviceToken', deviceToken);
        if (!deviceToken) {
          logger.warn('getSubscriberId returned empty token, skipping register-device');
          return;
        }
  
        var registerUrl =
          (config.inactiveRegisterDeviceUrl && String(config.inactiveRegisterDeviceUrl).trim()) ||
          '/apps/common/v1/register-device';
        var response = await fetch(registerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deviceToken: deviceToken }),
        });

        logger.log('handleSixtyDaysInactive: API Called', response);
  
        if (response.ok) {
          try { localStorage.setItem(LS_KEY, today); } catch (e) { /* storage unavailable */ }
          logger.log('Register device success, token:', deviceToken);
        } else {
          logger.error('Register device API error:', response.status, response.statusText);
        }
      } catch (err) {
        logger.error('Register device error:', err);
      }
    });
  }

  function handleNotPurchased() {
    if (!config.events.notPurchasedEnabled) return;

    const count = getMetafieldNumber('total_order_count');
    logger.log('Not purchased — orderCount:', count);

    if (count == 0 && config.customerLoggedIn) {
      var trigger = {
        campaign_name: 'notpurchased',
        event_name:    'notpurchased',
        // profile_id: '<string>'  // optional — auto-set from config.customerEmail
      };
      fireEvent(trigger);
      logger.log('Fired notpurchased');
    }
  }

  /**
   * Category Dropoff — fires after the customer spends the configured time on a
   * collection page without navigating away.
   *
   * Uses a single setTimeout (not setInterval) calibrated to the remaining time
   * since the customer first landed on this collection, so it fires precisely
   * once at the right moment even when the customer returns within a session.
   */
  function handleCategoryDropoff() {
    if (!config.events.categoryDropoffEnabled) return;
    if (config.currentTemplate !== 'collection') return;

    const sessionKey   = SESSION_KEYS.CATEGORY_DROPOFF_FIRED + '_' + config.collectionHandle;
    const entryTimeKey = SESSION_KEYS.PLP_ENTRY_TIME + '_' + config.collectionHandle;

    if (sessionFired(sessionKey)) return;

    // Record (or recall) the entry timestamp for this collection
    let entryTime;
    try {
      const stored = sessionStorage.getItem(entryTimeKey);
      if (stored) {
        entryTime = parseInt(stored, 10);
      } else {
        entryTime = Date.now();
        sessionStorage.setItem(entryTimeKey, String(entryTime));
      }
    } catch (e) {
      entryTime = Date.now();
    }

    const thresholdMs = (config.categoryDropoffMinutes || 2) * 60 * 1000;
    const remaining   = thresholdMs - (Date.now() - entryTime);

    function fireCategoryDropoff() {
      var trigger = {
        campaign_name: 'categorydropoff',
        event_name:    'category_browse_event',
        // profile_id: '<string>'  // optional — auto-set from config.customerEmail
        data: {
          categoryname: config.collectionName,
          categorylink: config.collectionLink,
        },
      };
      fireEvent(trigger);
      markSessionFired(sessionKey);
      logger.log('Fired categorydropoff for:', config.collectionHandle);
    }

    // Already met the threshold (e.g. returning to same page within session)
    if (remaining <= 0) {
      fireCategoryDropoff();
      return;
    }

    const timer = setTimeout(fireCategoryDropoff, remaining);
    window.addEventListener('beforeunload', function () { clearTimeout(timer); }, { once: true });

    logger.log('Category dropoff timer started:', config.collectionHandle, '(' + (remaining / 1000).toFixed(0) + 's remaining)');
  }

  /**
   * Browse Abandonment — fires on product pages.
   *
   * Start ("browse"):     after configured minutes with no add-to-cart.
   * Stop  ("add-to-cart"): immediately when customer adds anything to cart.
   */
  function handleBrowseAbandonment() {
    if (!config.events.browseAbandonmentEnabled) return;
    if (config.currentTemplate !== 'product') return;

    const product = config.product;
    if (!product || !product.name) return;

    logger.log('Browse abandonment tracker started:', product.name);

    createDwellTracker({
      thresholdMs: (config.browseAbandonmentMinutes || 1) * 60 * 1000,
      onStart: function () {
        var trigger = {
          campaign_name: 'browseabandonment60218',
          event_name:    'browse',
          // profile_id: '<string>'  // optional — auto-set from config.customerEmail
          data: {
            productname:     product.name,
            price:           product.price,
            productlink:     product.url,
            productimage:    product.image,
            notificationurl: product.url,
            imageurl:        product.image,
            bigimageurl:     product.imageLarge || product.image,
          },
        };
        fireEvent(trigger);
        logger.log('Fired browseabandonment start after', config.browseAbandonmentMinutes, 'min');
      },
      onStop: function () {
        var trigger = {
          campaign_name: 'browseabandonment60218',
          event_name:    'add-to-cart',
          // profile_id: '<string>'  // optional — auto-set from config.customerEmail
        };
        fireEvent(trigger);
        logger.log('Fired browseabandonment stop (add-to-cart)');
      },
    });
  }

  /**
   * Web TV Dropoff — fires on configured Live TV page(s).
   *
   * Start ("livetv_browse_event"):    after configured minutes with no add-to-cart.
   * Stop  ("livetv_activity_event"):  immediately when customer adds anything to cart.
   */
  function handleWebTVDropoff() {
    if (!config.events.webTVDropoffEnabled) return;
    if (config.currentTemplate !== 'page') return;

    const handles = Array.isArray(config.webTVPageHandles)
      ? config.webTVPageHandles.map(function (/** @type {string} */ h) { return h.trim(); }).filter(Boolean)
      : [];

    if (!handles.length) return;

    const currentHandle = (config.pageHandle || '').trim();
    if (!handles.some(function (/** @type {string} */ h) { return h === currentHandle; })) {
      logger.log('Page "' + currentHandle + '" not in webTV handles, skipping');
      return;
    }

    logger.log('WebTV dropoff tracker started for page:', currentHandle);

    createDwellTracker({
      thresholdMs: (config.webTVDropoffMinutes || 1) * 60 * 1000,
      onStart: function () {
        var trigger = {
          campaign_name: 'webtvdropoff',
          event_name:    'livetv_browse_event',
          // profile_id: '<string>'  // optional — auto-set from config.customerEmail
        };
        fireEvent(trigger);
        logger.log('Fired webtvdropoff start after', config.webTVDropoffMinutes, 'min on:', currentHandle);
      },
      onStop: function () {
        var trigger = {
          campaign_name: 'webtvdropoff',
          event_name:    'livetv_activity_event',
          // profile_id: '<string>'  // optional — auto-set from config.customerEmail
        };
        fireEvent(trigger);
        logger.log('Fired webtvdropoff stop (add-to-cart) on:', currentHandle);
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Event Handler: Notify Me — PushEngage back-in-stock / inventory alert
  //
  // Listens for clicks on any [data-notifydata] button via event delegation.
  // The button's data-notifydata attribute must contain valid JSON with the
  // product fields written by notify-me.liquid / buy-shop-button.liquid.
  //
  // Calls PushEngage.addAlert() with the inventory alert payload.
  // image_link / big_image_link use the ShopLC notification logo (static).
  // ---------------------------------------------------------------------------

  function handleNotifyMe() {
    if (!config.events.notifyMeEnabled) return;

    // Static ShopLC logo used as the notification icon image
    var LOGO_URL = window.location.origin + '/cdn/shop/files/ShopLC-Logo_1.svg?height=36&v=1763030401';

    document.addEventListener('click', function (/** @type {MouseEvent} */ e) {
      var button = /** @type {HTMLElement | null} */ (
        (/** @type {HTMLElement} */ (e.target)).closest('[data-notifydata]')
      );
      if (!button) return;

      /** @type {{ product_id?: string, variant_id?: string, link?: string, price?: any, productname?: string, productlink?: string, productimage?: string, title?: string }} */
      var data;
      try {
        data = JSON.parse(button.dataset.notifydata || '');
      } catch (err) {
        logger.error('Failed to parse data-notifydata:', err);
        return;
      }

      var alert = {
        type:          'inventory',
        product_id:    String(data.product_id || ''),   // variant SKU
        variant_id:    String(data.variant_id || ''),   // variant ID
        link:          data.link || '',                  // product URL
        price:         data.price.replace(/[^\d.]/g, "") || 0,                  // price in cents
        productname:   data.productname || '',           // product name
        productlink:   data.productlink || '',           // product URL
        productimage:  data.productimage || '',          // product image URL
        title:         data.title || '',                 // product title
        availability: "outOfStock",
        image_link:    LOGO_URL,
        big_image_link: LOGO_URL,
      };

      logger.log('NotifyMe alert payload:', alert);

      // @ts-ignore
      window.PushEngage = window.PushEngage || [];
      // @ts-ignore
      window.PushEngage.push(function () {
        // @ts-ignore
        window.PushEngage.addAlert(alert)
          .then(function (/** @type {any} */ response) {
            logger.log('NotifyMe addAlert success:', response);
          })
          .catch(function (/** @type {{ message?: string, details?: any }} */ err) {
            logger.error('addAlert failed:', err.message, err.details);
          });
      });
    });

    logger.log('NotifyMe click handler registered');
  }

  /**
   * Price Drop — registers a PushEngage price-drop alert when the customer
   * adds a product to cart on a PDP.
   *
   * Listens for CartAddEvent.  On fire it calls PushEngage.addAlert() with
   * type "price_drop" so the customer is notified if the product price falls
   * below the current price.
   *
   * @param {Event} e - CartAddEvent dispatched after an add-to-cart
   */
  // @ts-ignore
  function handlePriceDrop(e) {
    if (!config.events.priceDropEnabled) return;
    if (config.currentTemplate !== 'product') return;

    var product = config.product;
    if (!product || !product.name) return;

    var variantId = String(product.variantId || '');
    var productId = String(product.id || '');

    var LOGO_URL = window.location.origin + '/cdn/shop/files/ShopLC-Logo_1.svg?height=36&v=1763030401';

    var alert = {
      type:            'price_drop',
      profile_id:      config.customerEmail || '',
      product_id:      product.sku || productId,
      variant_id:      variantId,
      link:            product.url,
      price:           product.priceRaw || 0,
      alert_price:     product.priceRaw || 0,
      availability:    product.available === false ? 'outOfStock' : 'inStock',
      productname:     product.name,
      notificationurl: product.url,
      productimage:    product.image,
      image_link:      LOGO_URL,
      big_image_link:  LOGO_URL,
    };
    // TODO: remove profile_id from the alert if empty
    if(!config.customerEmail) {
      delete alert.profile_id;
    }

    logger.log('Price drop alert payload:', alert);

    // @ts-ignore
    window.PushEngage = window.PushEngage || [];
    // @ts-ignore
    window.PushEngage.push(function () {
      // @ts-ignore
      window.PushEngage.addAlert(alert)
        .then(function (/** @type {any} */ response) {
          logger.log('Price drop addAlert success:', response);
        })
        .catch(function (/** @type {{ message?: string, details?: any }} */ err) {
          logger.error('Price drop addAlert failed:', err.message, err.details);
        });
    });
  }

  /**
   * Cart Abandonment (60218) — PDP add-to-cart stop signal.
   *
   * Fires when the customer adds the current PDP product to the cart so
   * PushEngage stops the cart-abandonment drip for this session.
   *
   * Uses config.product (populated by pushengage-data.liquid on PDP) for the
   * trigger data; fireEvent() auto-attaches profile_id from config.customerEmail.
   */
  // @ts-ignore
  function handleCartAbandonment() {
    if (!config.events.cartAbandonmentEnabled) return;
    if (config.currentTemplate !== 'product') return;

    var product = config.product;
    if (!product || !product.name) return;

    var trigger = {
      campaign_name: 'cartabandonment60218',
      event_name:    'add-to-cart',
      // profile_id auto-attached by fireEvent() from config.customerEmail
      data: {
        productname:     product.name,
        price:           String(product.price || ''),
        productimage:    product.image,
        notificationurl: product.url,
        imageurl:        product.image,
        bigimageurl:     product.imageLarge || product.image,
      },
    };

    fireEvent(trigger);
    logger.log('Fired cartabandonment60218 add-to-cart', trigger);
  }

  // ---------------------------------------------------------------------------
  // Browser Notification API — request push permission
  // ---------------------------------------------------------------------------

  /**
   * Prompts the user to allow browser notifications when permission is still
   * "default". Skips if unsupported, already granted, or previously denied.
   *
   * Some browsers block permission prompts without a user gesture; in that case
   * we retry once on the first click anywhere on the page.
   */
  function requestNotificationPermission() {
    if (!('Notification' in window)) {
      logger.log('Notification API not supported in this browser');
      return;
    }

    if (Notification.permission === 'granted') {
      logger.log('Notification permission already granted');
      return;
    }

    if (Notification.permission === 'denied') {
      logger.log('Notification permission previously denied');
      return;
    }

    function promptPermission() {
      Notification.requestPermission()
        .then(function (permission) {
          logger.log('Notification permission result:', permission);
        })
        .catch(function (err) {
          logger.error('Notification permission request failed:', err);
        });
    }

    promptPermission();

    // Fallback: browsers may block non-gesture prompts — retry on first click
    document.addEventListener(
      'click',
      function onFirstClick() {
        document.removeEventListener('click', onFirstClick);
        if (Notification.permission === 'default') {
          logger.log('Retrying notification permission after user interaction');
          promptPermission();
        }
      },
      { once: true }
    );
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function init() {
    if (!config.enabled) {
      logger.log('PushEngage disabled');
      return;
    }

    requestNotificationPermission();


    logger.log('Init — template:', config.currentTemplate, '| loggedIn:', config.customerLoggedIn);

    // These run for ALL visitors (no login required).
    // fireEvent() / addAlert() use window.PushEngage.push() so calls are
    // queued and processed automatically once the SDK loads.
    handleNotifyMe();
    handleCategoryDropoff();
    handleBrowseAbandonment();
    handleWebTVDropoff();

    document.addEventListener('click', (e) => {
      if (config.currentTemplate !== 'product') return;
      // @ts-ignore
      const addToCartBtn = e.target.closest('.button.add-to-cart-button');
      logger.log('addToCartBtn button clicked');

      // Add to cart click
      if (addToCartBtn) {
        // @ts-ignore
        handlePriceDrop();
        handleCartAbandonment();
      }
    });

    // Customer metafield events require a logged-in customer.
    if (!config.customerLoggedIn) return;

    handleLooseGemstoneDropoff();
    handleChairmanVaultDropoff();
    handleThirdPurchase();
    handleSecondPurchase();
    handleSixtyDaysInactive();
    handleNotPurchased();

    document.addEventListener('wishlist:updated', (e) => {
      if (config.currentTemplate !== 'product') return;

      // access data
      // @ts-ignore
      const data = e.detail;
      if(data.action == 'added') {
        // @ts-ignore
        logger.log('Wishlist Added:', e.detail);
        // @ts-ignore
        handlePriceDrop();
      }
      // your logic here
    });

  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  // @ts-ignore
  window.ShopLCPushEngage.fireEvent = fireEvent;
  // @ts-ignore
  window.ShopLCPushEngage.init = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
